import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../constant.js';
import './CustomerReferralHistory.css';

function CustomerReferralHistory() {
    const [currentPage, setCurrentPage] = useState(1);
    const [referrals, setReferrals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [summary, setSummary] = useState({
        total_referrals: 0,
        successful_referrals: 0,
        pending_referrals: 0
    });
    const navigate = useNavigate();

    // Fetch referral data
    const fetchReferralHistory = async (page = 1) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('auth_token');
            
            const response = await fetch(
                `${API_URL}/api/customer/referral-history?page=${page}&per_page=10`, 
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to fetch referral history: ${response.status}`);
            }

            const data = await response.json();
            console.log('API Response:', data); // Debug log
            
            // Check if response has referrals array
            if (data.referrals || Array.isArray(data.data?.referrals)) {
                // Handle both response structures
                const referralsData = data.referrals || data.data?.referrals || [];
                const paginationData = data.pagination || data.data?.pagination || {};
                
                setReferrals(referralsData);
                
                // Calculate summary from referrals
                const totalReferrals = paginationData.total || referralsData.length;
                const successfulReferrals = referralsData.filter(ref => 
                    ref.status === 'verified' || ref.is_verified === true
                ).length;
                const pendingReferrals = referralsData.filter(ref => 
                    ref.status === 'pending' || ref.is_verified === false
                ).length;
                
                setSummary({
                    total_referrals: totalReferrals,
                    successful_referrals: successfulReferrals,
                    pending_referrals: pendingReferrals
                });
                
                setTotalPages(paginationData.last_page || 1);
                setTotalItems(paginationData.total || referralsData.length);
                setError('');
            } else if (data.data) {
                // Handle if data is nested under data property
                setReferrals(data.data.referrals || []);
                setSummary({
                    total_referrals: data.data.summary?.total_referrals || 0,
                    successful_referrals: data.data.summary?.successful_referrals || 0,
                    pending_referrals: data.data.summary?.pending_referrals || 0
                });
                setTotalPages(data.data.pagination?.last_page || 1);
                setTotalItems(data.data.pagination?.total || 0);
                setError('');
            } else {
                // If response doesn't have expected structure but might be valid
                if (Array.isArray(data)) {
                    setReferrals(data);
                    const successful = data.filter(ref => 
                        ref.status === 'verified' || ref.is_verified === true
                    ).length;
                    const pending = data.filter(ref => 
                        ref.status === 'pending' || ref.is_verified === false
                    ).length;
                    setSummary({
                        total_referrals: data.length,
                        successful_referrals: successful,
                        pending_referrals: pending
                    });
                    setTotalPages(1);
                    setTotalItems(data.length);
                    setError('');
                } else {
                    throw new Error('Invalid response format from server');
                }
            }
            
        } catch (err) {
            setError(err.message);
            console.error('Error fetching referral history:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReferralHistory(1);
    }, []);

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        fetchReferralHistory(newPage);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (e) {
            return dateString;
        }
    };

    const getStatusText = (referral) => {
        // Check both status and is_verified fields
        if (referral.status === 'verified' || referral.is_verified === true) {
            return 'Active';
        } else if (referral.status === 'pending' || referral.is_verified === false) {
            return 'Pending';
        }
        return referral.status || 'Pending';
    };

    const getStatusClass = (referral) => {
        if (referral.status === 'verified' || referral.is_verified === true) {
            return 'active';
        } else if (referral.status === 'pending' || referral.is_verified === false) {
            return 'pending';
        }
        return 'pending';
    };

    // Generate pagination page numbers
    const generatePageNumbers = () => {
        const pageNumbers = [];
        
        if (totalPages <= 5) {
            // Show all pages if total pages is 5 or less
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            // Always show first page
            pageNumbers.push(1);
            
            // Calculate start and end of middle pages
            let start = Math.max(2, currentPage - 1);
            let end = Math.min(totalPages - 1, currentPage + 1);
            
            // Adjust if we're near the start
            if (currentPage <= 3) {
                end = 4;
            }
            
            // Adjust if we're near the end
            if (currentPage >= totalPages - 2) {
                start = totalPages - 3;
            }
            
            // Add ellipsis after first page if needed
            if (start > 2) {
                pageNumbers.push('...');
            }
            
            // Add middle pages
            for (let i = start; i <= end; i++) {
                pageNumbers.push(i);
            }
            
            // Add ellipsis before last page if needed
            if (end < totalPages - 1) {
                pageNumbers.push('...');
            }
            
            // Always show last page
            pageNumbers.push(totalPages);
        }
        
        return pageNumbers;
    };

    const LoadingIcon = () => (
        <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading referral history...</p>
        </div>
    );

    const handleRetry = () => {
        fetchReferralHistory(currentPage);
    };

    return (
        <div className="customer-referral-history-container">


            {/* Summary Cards */}
            {/* <div className="summary-cards">
                <div className="summary-card">
                    <h3>Total Referrals</h3>
                    <p className="summary-number">{summary.total_referrals}</p>
                </div>
                <div className="summary-card">
                    <h3>Active</h3>
                    <p className="summary-number">{summary.successful_referrals}</p>
                </div>
                <div className="summary-card">
                    <h3>Pending</h3>
                    <p className="summary-number">{summary.pending_referrals}</p>
                </div>
            </div> */}

            {/* Table Content */}
            <div className="table-container2">
                {loading ? (
                    <LoadingIcon />
                ) : error ? (
                    <div className="error-container">
                        <div className="error-icon">⚠️</div>
                        <h3>Error Loading Data</h3>
                        <p>{error}</p>
                        <button className="retry-btn" onClick={handleRetry}>
                            Retry
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="table-responsive">
                            <table className="referral-table">
                                <thead>
                                    <tr>
                                        <th>No</th>
                                        <th>Customer Name</th>
                                        {/* <th>Email</th> */}
                                        {/* <th>Phone</th> */}
                                        <th>Location</th>
                                        <th>Status</th>
                                        <th>Joined Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {referrals.length > 0 ? (
                                        referrals.map((referral, index) => {
                                            const itemNumber = (currentPage - 1) * 10 + index + 1;
                                            
                                            return (
                                                <tr key={referral.customer_id || referral.id || index}>
                                                    <td>{itemNumber}</td>
                                                    <td className="name-cell">
                                                        {referral.name || 'N/A'}
                                                    </td>
                                                    {/* <td>{referral.email || 'N/A'}</td> */}
                                                    {/* <td>{referral.phone_number || 'N/A'}</td> */}
                                                    <td>
                                                        {referral.country && referral.region 
                                                            ? `${referral.country}, ${referral.region}`
                                                            : referral.country || referral.region || 'N/A'}
                                                    </td>
                                                    <td>
                                                        <span className={`status-badge ${getStatusClass(referral)}`}>
                                                            {getStatusText(referral)}
                                                        </span>
                                                    </td>
                                                    <td>{formatDate(referral.joined_date || referral.created_at)}</td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="7">
                                                <div className="empty-state">
                                                    <div className="empty-icon">
                                                        📊
                                                    </div>
                                                    <h3>No Referrals Found</h3>
                                                    <p>
                                                        You haven't made any referrals yet. Start referring to earn rewards!
                                                    </p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && referrals.length > 0 && (
                            <div className="pagination-container">
                                <div className="pagination-info">
                                    Showing {(currentPage - 1) * 10 + 1} to {Math.min(currentPage * 10, totalItems)} of {totalItems} entries
                                </div>
                                <div className="pagination">
                                    {/*<button 
                                        className="page-btn prev-next first-page" 
                                        onClick={() => handlePageChange(1)}
                                        disabled={currentPage === 1}
                                        title="First Page"
                                    >
                                        «
                                    </button>*/}
                                    {/* <button 
                                        className="page-btn prev-next" 
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                    >
                                        ← Previous
                                    </button> */}
                                    
                                    <div className="page-numbers">
                                        {generatePageNumbers().map((pageNum, index) => {
                                            if (pageNum === '...') {
                                                return (
                                                    <span key={`ellipsis-${index}`} className="page-ellipsis">
                                                        ...
                                                    </span>
                                                );
                                            }
                                            
                                            return (
                                                <button
                                                    key={pageNum}
                                                    className={`page-btn ${currentPage === pageNum ? "active" : ""}`}
                                                    onClick={() => handlePageChange(pageNum)}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    
                                    {/* <button 
                                        className="page-btn prev-next" 
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                    >
                                        Next →
                                    </button> */}
                                    {/* <button 
                                        className="page-btn prev-next last-page" 
                                        onClick={() => handlePageChange(totalPages)}
                                        disabled={currentPage === totalPages}
                                        title="Last Page"
                                    >
                                        »
                                    </button> */}
                                </div>
                                
                                {/* <div className="page-jump">
                                    <span>Go to page: </span>
                                    <input
                                        type="number"
                                        min="1"
                                        max={totalPages}
                                        defaultValue={currentPage}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                const page = parseInt(e.target.value);
                                                if (page >= 1 && page <= totalPages) {
                                                    handlePageChange(page);
                                                }
                                            }
                                        }}
                                        className="page-input"
                                    />
                                </div> */}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default CustomerReferralHistory;